import { Button } from '@/components/Button';
import { InfiniteScrollList } from '@/components/InfiniteScrollList';
import { PageHeader } from '@/components/PageHeader';
import { useEventStore } from '@/stores/useEventStore';
import { Event } from '@/types/event';
import { isPC } from '@/utils/device';
import { formatDateTime } from '@/utils/formatDateTime';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';
import { useEffect } from 'react';
import { FaCalendar, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// Animations variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, when: 'beforeChildren' },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

// Composant EventManagementCard pour toutes les tailles d'écran
const EventManagementCard = ({
  event,
  onEdit,
}: {
  event: Event;
  onEdit: (event: Event) => void;
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'future':
        return 'bg-blue-100 text-linko-light';
      case 'ongoing':
        return 'bg-green-100 text-green-700';
      case 'past':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'public'
      ? 'bg-green-100 text-green-700'
      : 'bg-orange-100 text-orange-700';
  };

  return (
    <motion.div
      className="flex h-48 flex-col rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md"
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Header avec infos principales */}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-gray-900">{event.name}</h3>
          <p className="mt-1 truncate text-sm text-gray-600">
            {formatDateTime(event.date)}
          </p>
          <p className="truncate text-sm text-gray-500">
            {t('listEvents:list.total_users')}: {event.total_users}
            {event.maximum_participants && (
              <>
                <span className="text-gray-400">/</span>
                {event.maximum_participants}
              </>
            )}
          </p>
        </div>

        <div className="ml-4">
          <FaCalendar className="size-5 text-gray-400" />
        </div>
      </div>

      {/* Badges */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(event.status)}`}
        >
          {t(`common:status.${event.status}`)}
        </span>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getTypeColor(event.type)}`}
        >
          {t(`common:type.${event.type}`)}
        </span>
      </div>

      {/* Action principale */}
      <button
        onClick={() => onEdit(event)}
        className="mt-auto w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
      >
        {t('common:edit')}
      </button>
    </motion.div>
  );
};

export const EventsManagement = () => {
  const navigate = useNavigate();
  const { events, loading, fetchEvents, filters, setFilters } = useEventStore();

  useEffect(() => {
    const fetchEventsData = async () => {
      if (filters.current_page === 1) {
        const perPage = isPC() ? 20 : 10;

        if (filters.per_page !== perPage) {
          setFilters({ per_page: perPage });
        }

        await fetchEvents({
          current_page: filters.current_page,
          per_page: perPage,
          sort_field: filters.sort_field,
          sort_order: filters.sort_order,
        });
      }
    };

    fetchEventsData();
  }, [
    fetchEvents,
    filters.current_page,
    filters.per_page,
    filters.sort_field,
    filters.sort_order,
    setFilters,
  ]);

  const fetchMoreData = async () => {
    if (loading || (filters.current_page || 1) >= (filters.last_page || 1)) {
      return;
    }

    const nextPage = (filters.current_page || 1) + 1;
    await fetchEvents({
      ...filters,
      current_page: nextPage,
    });
  };

  const handleEdit = (event: Event) => {
    navigate(`${event.id}`);
  };

  return (
    <div id="scrollableDiv" className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between lg:gap-0"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <PageHeader title={t('listEvents:list.title')} count={events.length} />
        <Button
          onClick={() => navigate(`create`)}
          className="flex w-full items-center justify-center gap-2 lg:w-auto"
        >
          <FaPlus className="size-4" />
          {t('listEvents:list.create_event')}
        </Button>
      </motion.div>

      {/* Liste des événements */}
      {events.length === 0 && !loading ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="flex flex-col items-center justify-center px-4 pb-4"
        >
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-20 items-center justify-center rounded-full bg-gray-100">
              <FaCalendar className="size-10 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {t('listEvents:empty.title')}
            </h3>
            <p className="mb-6 max-w-sm text-sm text-gray-500">
              {t('listEvents:empty.description')}
            </p>
            <Button
              onClick={() => navigate('create')}
              className="bg-linko-light hover:bg-linko-dark"
            >
              {t('listEvents:list.create_event')}
            </Button>
          </div>
        </motion.div>
      ) : (
        <InfiniteScrollList
          dataLength={events.length}
          next={fetchMoreData}
          hasMore={(filters.current_page || 1) < (filters.last_page || 1)}
          total={events.length}
          scrollableTarget="scrollableDiv"
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="px-4"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence>
                {events.map(event => (
                  <motion.div key={event.id} variants={itemVariants}>
                    <EventManagementCard event={event} onEdit={handleEdit} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </InfiniteScrollList>
      )}
    </div>
  );
};
